import React, { useEffect, useState } from "react";
import { Card, Col, notification, Row, TreeSelect, List, Tag, Typography, Button } from "antd";
import { useParams } from "react-router-dom";
import { TenantService } from "../../Services";
import LAYERS_PERMISSIONS from "../../permissions/Layers.json";
import MARKER_PERMISSIONS from "../../permissions/Markers.json";
import { buildTreeData, extractChildIds } from "./Service";
import isEqual from "lodash/isEqual";

const { Text } = Typography;

const layersTreeData = buildTreeData(LAYERS_PERMISSIONS);
const markersTreeData = buildTreeData(MARKER_PERMISSIONS);

const isLayerPermission = permission => Object.keys(LAYERS_PERMISSIONS).some(node => node === permission);
const isMarkerPermission = permission => Object.keys(MARKER_PERMISSIONS).some(node => node === permission);
export const Permissions = () => {
	const { tenantName } = useParams();
	const [oldPermissions, setOldPermissions] = useState([]);
	const [newMarkerPermissions, setNewMarkerPermissions] = useState([]);
	const [newLayerPermissions, setNewLayerPermissions] = useState([]);

	const handleTenantConfigChange = data => {
		const existingMarkersPermissions = data.permissions.filter(isMarkerPermission);
		const existingLayersPermissions = data.permissions.filter(isLayerPermission);
		setNewMarkerPermissions(existingMarkersPermissions);
		setNewLayerPermissions(existingLayersPermissions);
		setOldPermissions(data.permissions);
	};
	useEffect(() => {
		const fetchTenantConfig = async () => {
			try {
				const { data } = await TenantService.getTenantPermissions({ tenantName });
				handleTenantConfigChange(data);
			} catch (error) {
				notification.error({ message: "Error fetching tenant config" });
				console.error("Error fetching tenant config:", error);
			}
		};
		fetchTenantConfig();
	}, [tenantName]);

	const handleMarkerPermissionsChange = selectedPermissions => {
		const childPermissions = extractChildIds(selectedPermissions, markersTreeData);
		const allSelectedPermissions = [...selectedPermissions, ...childPermissions];
		setNewMarkerPermissions(
			allSelectedPermissions.filter(id => !markersTreeData.some(node => node.id === id && node.children))
		);
	};

	const handleLayerPermissionsChange = selectedPermissions => {
		const childPermissions = extractChildIds(selectedPermissions, layersTreeData);
		const allSelectedPermissions = [...selectedPermissions, ...childPermissions];
		setNewLayerPermissions(
			allSelectedPermissions.filter(id => !layersTreeData.some(node => node.id === id && node.children))
		);
	};

	const isPermissionDeleted = permission => ![...newLayerPermissions, ...newMarkerPermissions].includes(permission);

	const isNewPermission = permission =>
		(newLayerPermissions.includes(permission) || newMarkerPermissions.includes(permission)) &&
		!oldPermissions.includes(permission);

	return (
		<Card title="Permissions">
			<Row gutter={[12, 12]}>
				<Col span={24}>
					<Row gutter={[16, 16]}>
						<Col span={12}>
							<Card title="Layers Permissions">
								<TreeSelect
									allowClear
									style={{ width: "100%" }}
									value={newLayerPermissions}
									dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
									treeData={layersTreeData}
									placeholder="Please select"
									maxTagCount={3}
									treeCheckable
									showCheckedStrategy={TreeSelect.SHOW_CHILD} // Show only child in the input
									treeDefaultExpandAll
									onChange={handleLayerPermissionsChange}
								/>
							</Card>
						</Col>

						<Col span={12}>
							<Card title="Markers Permissions">
								<TreeSelect
									allowClear
									showSearch
									maxTagCount={3}
									style={{ width: "100%" }}
									value={newMarkerPermissions}
									dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
									treeData={markersTreeData}
									placeholder="Please select"
									treeCheckable
									showCheckedStrategy={TreeSelect.SHOW_CHILD} // Show only child in the input
									treeDefaultExpandAll
									onChange={handleMarkerPermissionsChange}
								/>
							</Card>
						</Col>

						<Col span={24}>
							<Card
								title={
									<Row>
										<Col span={4}>
											<span>
												Selected Permissions (
												{newLayerPermissions.length + newMarkerPermissions.length})
											</span>
										</Col>
										<Col span={17} />
										<Col span={3}>
											<Button
												type="primary"
												onClick={async () => {
													try {
														const { data } = await TenantService.updateTenantPermissions({
															tenantName,
															permissions: [
																...newLayerPermissions,
																...newMarkerPermissions
															]
														});
														handleTenantConfigChange(data);
														notification.success({
															message: "Permissions updated successfully"
														});
													} catch (error) {
														notification.error({ message: "Error updating permissions" });
														console.error("Error updating permissions:", error);
													}
												}}
												disabled={isEqual(
													[...newLayerPermissions, ...newMarkerPermissions].sort(),
													oldPermissions.sort()
												)}
											>
												Save Permissions
											</Button>
										</Col>
									</Row>
								}
								style={{ height: "400px" }}
							>
								<List
									bordered
									dataSource={[
										...new Set([...newLayerPermissions, ...newMarkerPermissions, ...oldPermissions])
									]}
									renderItem={permission => {
										const isDeleted = isPermissionDeleted(permission);
										const isNew = isNewPermission(permission);
										const permissionColor = isDeleted ? "red" : isNew ? "green" : "blue";

										const handleRemove = () => {
											if (newLayerPermissions.includes(permission)) {
												setNewLayerPermissions(
													newLayerPermissions.filter(p => p !== permission)
												);
											} else if (newMarkerPermissions.includes(permission)) {
												setNewMarkerPermissions(
													newMarkerPermissions.filter(p => p !== permission)
												);
											}
										};

										const handleUndo = () => {
											if (isDeleted) {
												if (isLayerPermission(permission)) {
													setNewLayerPermissions([...newLayerPermissions, permission]);
												} else if (isMarkerPermission(permission)) {
													setNewMarkerPermissions([...newMarkerPermissions, permission]);
												}
											}
										};

										return (
											<List.Item>
												<Row>
													<Col>
														<Tag color={permissionColor}>
															<Text delete={isDeleted}>{permission}</Text>
														</Tag>
													</Col>
													<Col>
														{!isDeleted && (
															<Button type="link" onClick={handleRemove}>
																Remove
															</Button>
														)}
														{isDeleted && (
															<Button type="link" onClick={handleUndo}>
																Undo
															</Button>
														)}
													</Col>
												</Row>
											</List.Item>
										);
									}}
									style={{ maxHeight: "300px", overflowY: "auto" }}
								/>
							</Card>
						</Col>
					</Row>
				</Col>
			</Row>
		</Card>
	);
};

export default Permissions;
